import { watch, onMounted, onUnmounted, ref } from 'vue';
import { apiFetch } from '../services/api';
/**
 * Composable that dynamically updates the favicon <link> tag.
 * Supports both generated (letter-avatar) and custom favicon modes.
 */
export function useFavicon() {
  const faviconType = ref<'generated' | 'custom'>('generated');
  const faviconColor = ref('#3B82F6');
  const faviconTextColor = ref('#FFFFFF');
  const faviconCustomUrl = ref<string | undefined>(undefined);

  function updateFaviconLink() {
    let href: string;

    if (faviconType.value === 'custom' && faviconCustomUrl.value) {
      href = faviconCustomUrl.value;
    } else {
      // Generated favicon with cache busting
      const ts = Date.now();
      href = `/api/system/favicon?t=${ts}`;
    }

    // Find and update existing favicon link
    let link = document.querySelector('link[rel="icon"]') as HTMLLinkElement | null;
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = href;
  }

  async function loadFaviconSettings() {
    try {
      const settings = await apiFetch('/system/settings/favicon');
      faviconType.value = settings.type || 'generated';
      faviconColor.value = settings.color || '#3B82F6';
      faviconTextColor.value = settings.textColor || '#FFFFFF';
      faviconCustomUrl.value = settings.customUrl || undefined;
      updateFaviconLink();
    } catch {
      // Use defaults if settings can't be loaded
      updateFaviconLink();
    }
  }

  // Watch for favicon-related changes
  watch([faviconType, faviconColor, faviconTextColor, faviconCustomUrl], () => {
    updateFaviconLink();
  });

  // Listen for favicon settings changes from other components
  function onFaviconChanged(event: CustomEvent) {
    const { type, color, textColor, customUrl } = event.detail || {};
    if (type !== undefined) faviconType.value = type;
    if (color !== undefined) faviconColor.value = color;
    if (textColor !== undefined) faviconTextColor.value = textColor;
    if (customUrl !== undefined) faviconCustomUrl.value = customUrl;
  }

  onMounted(() => {
    // Listen for favicon-changed custom event from settings page
    window.addEventListener('favicon-changed', onFaviconChanged as EventListener);
  });

  onUnmounted(() => {
    window.removeEventListener('favicon-changed', onFaviconChanged as EventListener);
  });

  return {
    faviconType,
    faviconColor,
    faviconTextColor,
    faviconCustomUrl,
    updateFaviconLink,
    loadFaviconSettings,
  };
}
